/**
 * Компонент для обработки callback после авторизации
 * Используется для обработки редиректов от Supabase
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';
import { shouldRedirectToGame } from '../utils/authRedirect';
import { linkGuestGames } from '../api';

const AuthCallback: React.FC = () => {
  const { loading, user } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    // Не запускаем callback пока AuthContext загружается
    if (loading) {
      console.log('AuthCallback: Waiting for AuthContext to finish loading...');
      return;
    }

    // Защита от повторных запусков
    if (processed) {
      console.log('AuthCallback: Already processed, skipping...');
      return;
    }

    const handleAuthCallback = async () => {
      setProcessed(true); // Помечаем что начали обработку
      try {
        console.log('AuthCallback: AuthContext ready, processing callback...', { user: !!user });

        // Дополнительная проверка: убеждаемся что токен доступен
        if (supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            console.error('AuthCallback: No session found after auth');
            throw new Error('No session found');
          }
          console.log('AuthCallback: Session confirmed', { userId: session.user.id });
        }

        setStatus('success');

        // Проверяем guest параметры в URL (для Email Magic Link)
        const urlParams = new URLSearchParams(window.location.search);
        const urlGuestId = urlParams.get('guest_id');
        const urlGuestGameId = urlParams.get('guest_game_id');

        if (urlGuestId && urlGuestGameId) {
          try {
            console.log('AuthCallback: Found guest params in URL:', { urlGuestId, urlGuestGameId });

            // Связываем гостевые игры с пользователем
            await linkGuestGames(urlGuestId);
            console.log('✅ Successfully linked guest games from URL params');

            // Очищаем localStorage
            localStorage.removeItem('guest_id');
            localStorage.removeItem('guest_game_id');

            // Redirect на продолжение игры
            setTimeout(() => {
              window.location.href = `/?gameId=${urlGuestGameId}`;
            }, 1000);
            return;
          } catch (error) {
            console.error('Failed to link guest games from URL:', error);
            // Продолжаем обычный flow даже если связывание не удалось
          }
        }

        // Проверяем pending_guest_link из sessionStorage (для Google OAuth)
        const pendingGuestLink = sessionStorage.getItem('pending_guest_link');
        if (pendingGuestLink) {
          try {
            const { guest_id, guest_game_id } = JSON.parse(pendingGuestLink);
            console.log('AuthCallback: Found pending guest link:', { guest_id, guest_game_id });

            // Связываем гостевые игры с пользователем
            await linkGuestGames(guest_id);
            console.log('✅ Successfully linked guest games');

            // Очищаем данные
            sessionStorage.removeItem('pending_guest_link');
            localStorage.removeItem('guest_id');
            localStorage.removeItem('guest_game_id');

            // Redirect на продолжение игры
            setTimeout(() => {
              window.location.href = `/?gameId=${guest_game_id}`;
            }, 1000);
            return;
          } catch (error) {
            console.error('Failed to link guest games:', error);
            // Продолжаем обычный flow даже если связывание не удалось
          }
        }

        // Проверяем сохранённый redirect после логина (например, из expired session)
        const savedRedirect = localStorage.getItem('redirect_after_login');
        if (savedRedirect) {
          console.log('AuthCallback: Found saved redirect:', savedRedirect);
          localStorage.removeItem('redirect_after_login');
          setTimeout(() => {
            window.location.href = savedRedirect;
          }, 1000);
          return;
        }

        // Проверяем количество игр пользователя
        // Если игр нет (новый пользователь) → /magic, если есть → /my-games
        setTimeout(async () => {
          const isNewUser = await shouldRedirectToGame();

          if (isNewUser) {
            // Новый пользователь - сразу в игру
            console.log('AuthCallback: New user detected, redirecting to game');

            // Получаем user из Supabase session
            let playerName = 'Игрок';
            if (supabase) {
              const { data: { session } } = await supabase.auth.getSession();
              const currentUser = session?.user;
              playerName = currentUser?.user_metadata?.full_name || currentUser?.email?.split('@')[0] || 'Игрок';
              console.log('AuthCallback: Got user from Supabase:', { playerName, user: currentUser });
            }

            window.location.href = `/magic?playerName=${encodeURIComponent(playerName)}`;
          } else {
            // Возвращающийся пользователь - на список игр
            console.log('AuthCallback: Returning user detected, redirecting to my-games');
            window.location.href = '/my-games';
          }
        }, 1000);

      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    };

    handleAuthCallback();
  }, [loading, user]); // Перезапускаем когда loading изменится

  if (status === 'processing' || loading) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <h4 className="mb-2">Завершаем авторизацию...</h4>
          <p className="text-muted">Пожалуйста, подождите</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="text-success mb-3">
            <i className="bi bi-check-circle" style={{ fontSize: '4rem' }}></i>
          </div>
          <h4 className="text-success mb-2">Авторизация успешна!</h4>
          <p className="text-muted">Перенаправляем вас в ваши игры...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="bi bi-exclamation-circle" style={{ fontSize: '4rem' }}></i>
          </div>
          <h4 className="text-danger mb-2">Ошибка авторизации</h4>
          <p className="text-muted mb-3">{error || 'Произошла неизвестная ошибка'}</p>
          <a href="/" className="btn btn-primary">
            Вернуться на главную
          </a>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
